import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CommunityTabsProvider } from '../community/CommunityTabsProvider';
import { InnerhivePage } from '../InnerhivePage';
import { ResourcesState } from './ResourcesState';
import { Spinner } from '../components/Spinner';
import { Card } from '../components/Card';
import { Text } from '../components/Text';
import { IonButton, IonCol, IonGrid, IonRow } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { mediaMaxWidth } from '../theme/utils';
import { ResourceFilters } from './ResourceFilters';
import { useSearchParams } from '../utils/useSearchParams';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useEffect, useRef } from 'react';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { IResource } from './IResource';
import { ResourceRating } from './ResourceRating';
import { useToast } from '../components/useToast';
import { ShareResourceButton } from './ShareResourceButton';

const useStyles = createUseStyles(() => ({
    page: {
        // InnerhivePage padding 16px plus IonCard margin 10px
        // is too much, reduce by 6px for a total of 20px
        margin: -6,

        display: 'flex',
        flexDirection: 'column',
        marginBottom: 20
    },

    logo: {
        cursor: 'pointer',

        // 1st of 2 cols
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        paddingRight: 20,
        maxWidth: 280,

        // single col
        [mediaMaxWidth.sm]: {
            paddingRight: 'inherit',
            paddingBottom: 10,
            width: '75%',
            margin: 'auto'
        }
    },

    buttons: {
        margin: '5px 0 5px -12px',
        right: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    }
}));

export const ResourcesPage: React.FC = () => {
    const classes = useStyles();

    const topOfPageRef = useRef<HTMLDivElement>(null);

    const resourcesState = useInstance(ResourcesState);
    useSubscription(() => resourcesState);

    const history = useInstance(HistoryViewModel);

    const searchParams = useSearchParams();
    const selectedCategory = searchParams.get('category') || undefined;

    const tabsProvider = useInstance(CommunityTabsProvider);

    const mixpanel = useInstance(MixPanelEventEmitter);

    const [showToast] = useToast();

    useEffect(() => {
        if (mixpanel.getLastEvent() !== 'Resource Opened') {
            mixpanel.track('Resources Tab Opened');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filterTo = (category: string | undefined) => {
        resourcesState.filterTo({ category, trackingFrom: 'List' });
        topOfPageRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };

    const gotoDetailPage = (resource: IResource) => {
        history.push(`/resources/${resource.slug}`);
    };

    const onRate = async ({ resourceId, rating }: { resourceId: string, rating?: number }) => {
        try {
            await resourcesState.rateResource({ resourceId, rating });
        } catch (err: any) {
            showToast({ message: err.displayMessage || 'Sorry something went wrong', duration: 3000 });
        }
    };

    const onWebsiteOpened = (resource: IResource) => {
        mixpanel.track('Resource Website Opened', () => ({
            'Name': resource.name,
            'Category': resource.category,
            'Opened From': 'List'
        }));
    };

    const resources = resourcesState.getResources(selectedCategory);

    return (
        <InnerhivePage title="Community" tabs={tabsProvider.getTabs()} skipRequireName>

            <div className={classes.page} data-type="resource-list">

                <div ref={topOfPageRef} style={{scrollMargin: 20}} />

                <div style={{padding: '5px 10px 20px'}}>
                    Discover caregiver-recommended resources, shared and elevated by the Innerhive Community, to support you on your journey.
                    Is&nbsp;something missing? Submit it <a href="https://tally.so/r/mYxvg0" target="_blank">here</a>.
                </div>

                <ResourceFilters />

                {resourcesState.isLoaded() &&
                    resources.map(resource => (
                        <Card key={resource.resourceId} padded>
                            <IonGrid style={{padding: 0}}>
                                <IonRow>

                                    <IonCol style={{padding: 0}} size="12" sizeSm="6" sizeMd="4">

                                        <div className={classes.logo}  onClick={() => gotoDetailPage(resource)}>
                                            <img src={resource.logoUrl} alt="logo" />
                                        </div>

                                    </IonCol>

                                    <IonCol style={{padding: 0}} size="12" sizeSm="6" sizeMd="8">

                                        <div style={{cursor: 'pointer'}} onClick={() => gotoDetailPage(resource)}>
                                            <Text type="title">{resource.name}</Text>
                                        </div>

                                        <div style={{margin: '10px 0 15px'}}>
                                            <ResourceRating
                                                resource={resource}
                                                onRate={rating => onRate({ resourceId: resource.resourceId, rating })}
                                                trackingFrom="List"
                                                buttonSize="small"
                                            />
                                        </div>

                                        <div data-type="category" style={{cursor: 'pointer', margin: '10px 0'}} onClick={() => filterTo(resource.category)}>
                                            <Text type="subtitle"><span style={{fontSize: 14}}>{resource.category.toUpperCase()}</span></Text>
                                        </div>

                                        <Text>{resource.summary}</Text>

                                        <div className={classes.buttons}>
                                            <div>
                                                <IonButton data-type="learn-more-button" mode="ios" size="small" fill="clear" color="dark" onClick={() => gotoDetailPage(resource)}>
                                                    <b>Learn More</b>
                                                </IonButton>

                                                {resource.website &&
                                                    <IonButton mode="ios" size="small" fill="clear" color="dark" href={resource.website} onClick={() => onWebsiteOpened(resource)} target="_blank">
                                                        <b>Website</b>
                                                    </IonButton>
                                                }
                                            </div>

                                            <ShareResourceButton
                                                data-type="share-button"
                                                mode="ios"
                                                size="small"
                                                color="light"
                                                resource={resource}
                                                trackingFrom="List"
                                            />
                                        </div>

                                    </IonCol>

                                </IonRow>
                            </IonGrid>
                        </Card>
                    ))
                }
            </div>

            {!resourcesState.isLoaded() &&
                <Spinner centered />
            }
        </InnerhivePage>
    );
};
